// @flow
import React, { type Ref, type ElementProps } from 'react';
import debounce from 'lodash/debounce';
import IntersectionObserver from 'app/packages/intersection-observer-component';
import videoLogV2ToV1Adapter from 'app/common/react/components/video/videoLogV2ToV1Adapter';
import VideoWrapper from 'app/common/react/components/video/VideoWrapper';
import { GRANULAR_INTERSECTION_OBSERVER_THRESHOLD } from 'app/common/react/components/video/constants';
import { track } from 'app/common/lib/react/analytics';
import { Video } from 'gestalt';
import type { MaybeVideoEvent, VideoEvent } from 'app/packages/pinterest-flow-types/video';

const ALLOWED_QUARTILES = [0, 0.25, 0.5, 0.75, 0.95, 0.97];
const ALLOWED_VIEWABILITY_THRESHOLDS = [0, 0.5, 0.8, 1.0];
const INTERSECTION_OBSERVER_OPTIONS: IntersectionObserverOptions = {
  threshold: ALLOWED_VIEWABILITY_THRESHOLDS,
};
const GRANULAR_INTERSECTION_OBSERVER_OPTIONS: IntersectionObserverOptions = {
  rootMargin: '-64px 0px 0px 0px',
  threshold: GRANULAR_INTERSECTION_OBSERVER_THRESHOLD,
};

const MILLISECONDS_IN_SECOND = 1000;

export const VideoPlaybackState = Object.freeze({
  LOADING: 0,
  PLAYING: 1,
  PAUSED: 2,
  STALLING: 3,
});
type Props = {|
  inAdsDesktopVideoExperiment?: boolean,
  rootMargin?: string,
  contextLogData: {|
    component: $Values<PCONST.thrift.ComponentType>,
    view?: $Values<PCONST.thrift.ViewType>,
    viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
    objectId: string,
    video_id: string,
  |},
  ...ElementProps<typeof VideoWrapper>,
|};

type State = {|
  seeking: boolean,
|};

function currentUnixTimeInMs(): number {
  return new Date().getTime();
}

function floorRound(num: number, floors: Array<number>): ?number {
  for (let i = 0; i < floors.length; i += 1) {
    const floor = floors[i];
    const ceil = floors[i + 1];

    if (ceil === null || ceil === undefined) {
      return floor;
    }
    if (floor <= num && num < ceil) {
      return floor;
    }
  }
  return null;
}

/*
 * Floor round quartile percent value to one of the allowed quartiles.
 */
function floorRoundQuartilePercentValue(num: number): ?number {
  return floorRound(num, ALLOWED_QUARTILES);
}
/*
 * Floor round an intersection ratio to one of the allowed viewability thresholds.
 */
function floorRoundIntersectionRatio(num: number): ?number {
  return floorRound(num, ALLOWED_VIEWABILITY_THRESHOLDS);
}

function isAudible(props: Props) {
  return props.volume ? props.volume > 0 : false;
}

/*
 * Video Gestalt component with context logging that implements the following
 * spec: http://pinch.pinadmin.com/video-client-logging-spec
 *
 * Parts of the spec are still unimplemented. Left as an exercise to the reader:
 *   TODO(OVIDEO-50, OVIDEO-54): Implement STALLING playback state.
 *   TODO(OVIDEO-56): Make sure last interval is flushed when window fires unload event.
 */
export default class VideoWithContextLogging extends React.Component<Props, State> {
  static defaultProps = Video.defaultProps;

  constructor(props: Props) {
    super(props);
    this.state = { seeking: false };

    const nowMs = currentUnixTimeInMs();
    this.ready = false;
    this.handleWindowResize = debounce(this.handleWindowResize, 1000);
    this.videoWrapperRef = React.createRef();
    this.currentInterval = {
      videoDuration: 0,
      startTime: nowMs,
      endTime: nowMs,
      videoStartTime: 0,
      videoEndTime: 0,
      isAudible: isAudible(props),
      playbackState: this.getPlaybackState(props, this.state),
      quartile: -1,
      quartilePercentValue: 0,
      height: null,
      width: null,
      windowHeight: null,
      windowWidth: null,
      viewability: null,
    };
    this.videoTime = 0;
    this.seenQuartiles = new Set();
    this.fullscreen = false;
  }

  componentDidMount() {
    this.currentInterval = { ...this.currentInterval, ...this.getDimensions() };
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('beforeunload', this.handleClose);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const event = {};
    if (isAudible(prevProps) && !isAudible(this.props)) {
      event.isAudible = false;
    } else if (!isAudible(prevProps) && isAudible(this.props)) {
      event.isAudible = true;
    }

    const playbackState = this.getPlaybackState(this.props, this.state);
    if (this.getPlaybackState(prevProps, prevState) !== playbackState) {
      event.playbackState = playbackState;
    }

    if (Object.keys(event).length > 0) {
      this.startNewInterval(event);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('beforeunload', this.handleClose);
    // When the component unmounts itself such as scrolling the video far off screen
    // we should end the current interval and log it.
    this.startNewInterval();
  }

  getPlaybackState(props: Props, state: State): $Values<typeof VideoPlaybackState> {
    if (!this.ready) {
      return VideoPlaybackState.LOADING;
    }

    // TODO(OVIDEO-50, OVIDEO-54): Implement STALLING playback state.
    return props.playing && !state.seeking ? VideoPlaybackState.PLAYING : VideoPlaybackState.PAUSED;
  }

  getDimensions(): { height?: number, width?: number, windowHeight: number, windowWidth: number } {
    let height;
    let width;
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    if (typeof this.videoWrapperRef === 'object' && this.videoWrapperRef.current) {
      const videoWrapper = this.videoWrapperRef.current;
      if (!!videoWrapper.clientHeight && !!videoWrapper.clientWidth) {
        height = videoWrapper.clientHeight;
        width = videoWrapper.clientWidth;
      }
    }
    return {
      height,
      width,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    };
  }

  currentInterval: MaybeVideoEvent;
  videoWrapperRef: Ref<'div'>;
  videoTime: number;
  seenQuartiles: Set<number>;
  ready: boolean;
  fullscreen: boolean;

  logVideoEvent(
    eventType: $Values<PCONST.thrift.EventType>,
    maybeVideoEvent: MaybeVideoEvent,
  ): void {
    const { contextLogData } = this.props;
    const { height, width, windowHeight, windowWidth, viewability, ...rest } = maybeVideoEvent;

    // Only log video event if MaybeVideoEvent can be refined to VideoEvent.
    // Otherwise it may mean we're still waiting for some data.
    if (
      height != null &&
      width != null &&
      windowHeight != null &&
      windowWidth != null &&
      viewability != null
    ) {
      const videoEvent: VideoEvent = {
        ...rest,
        height,
        width,
        windowHeight,
        windowWidth,
        viewability,
      };
      track(
        eventType,
        videoLogV2ToV1Adapter({
          ...contextLogData,
          // If the video is currently in fullscreen overwrite the view type that was passed down
          view: this.fullscreen
            ? PCONST.thrift.ViewType.FULL_SCREEN_VIDEO_PLAYER
            : contextLogData.view,
          eventData: {
            videoEvent,
          },
        }),
      );
    }
  }

  // Fourth quartile events are explicitly logged. A 4th quartile event happens at the moment a video completes.
  logFourthQuartileEvent() {
    const nowMs = currentUnixTimeInMs();
    this.logVideoEvent(PCONST.thrift.EventType.VIDEO_SINGLE_QUARTILE, {
      ...this.currentInterval,
      startTime: nowMs,
      endTime: nowMs,
      videoStartTime: 0,
      videoEndTime: 0,
      quartile: 4,
      quartilePercentValue: 1.0,
    });
  }

  handleWindowResize = () => {
    //Ignore window resizes due to moving to fullscreen
    if (!this.fullscreen) {
      const dimensions = this.getDimensions();
      const { height, width } = this.currentInterval;

      if (dimensions.height !== height || dimensions.width !== width) {
        this.startNewInterval(dimensions);
      } else {
        this.currentInterval.windowHeight = dimensions.windowHeight;
        this.currentInterval.windowWidth = dimensions.windowWidth;
      }
    }
  };

  handlePlayheadDown = ({ event }: { event: SyntheticMouseEvent<HTMLDivElement> }) => {
    this.setState({ seeking: true });
    // Clear out quartiles so they can fire again if the user seeks backwards
    this.seenQuartiles = new Set();
  };

  handlePlayheadUp = ({ event }: { event: SyntheticMouseEvent<HTMLDivElement> }) => {
    this.setState({ seeking: false });
  };

  /*
   * Log current interval and start a new one with given interval data.
   */
  startNewInterval(newIntervalData?: $Shape<MaybeVideoEvent>) {
    const nowMs = currentUnixTimeInMs();
    this.logVideoEvent(PCONST.thrift.EventType.VIDEO_PLAYBACK_INTERVAL, {
      ...this.currentInterval,
      endTime: nowMs,
      videoEndTime: this.videoTime,
    });
    this.currentInterval = {
      ...this.currentInterval,
      ...newIntervalData,
      startTime: nowMs,
      videoStartTime: this.videoTime,
      endTime: -1,
      videoEndTime: -1,
    };
  }

  handleTimeChange = (args: { event: SyntheticEvent<HTMLVideoElement>, time: number }) => {
    const { seeking } = this.state;
    if (!this.currentInterval.videoDuration) {
      return;
    }

    const quartilePercentValue = floorRoundQuartilePercentValue(
      (args.time * MILLISECONDS_IN_SECOND) / this.currentInterval.videoDuration,
    );

    if (
      this.props.playing &&
      !seeking &&
      quartilePercentValue != null &&
      ALLOWED_QUARTILES.includes(quartilePercentValue) &&
      !this.seenQuartiles.has(quartilePercentValue)
    ) {
      const nowMs = currentUnixTimeInMs();
      this.logVideoEvent(PCONST.thrift.EventType.VIDEO_SINGLE_QUARTILE, {
        ...this.currentInterval,
        startTime: nowMs,
        endTime: nowMs,
        videoStartTime: 0,
        videoEndTime: 0,
        quartile: Math.floor(quartilePercentValue * 4),
        quartilePercentValue,
      });
      this.seenQuartiles.add(quartilePercentValue);
    }

    this.videoTime = args.time * MILLISECONDS_IN_SECOND; // We want to log video times in milliseconds

    if (this.props.onTimeChange) {
      this.props.onTimeChange(args);
    }
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  handleDurationChange = (args: $Subtype<{ duration: number }>) => {
    this.currentInterval.videoDuration = args.duration * MILLISECONDS_IN_SECOND;
    if (this.props.onDurationChange) {
      this.props.onDurationChange(args);
    }
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  handleReady = (args: *) => {
    if (!this.ready) {
      this.ready = true;
      this.startNewInterval({
        playbackState: this.getPlaybackState(this.props, this.state),
      });
    }
    if (this.props.onReady) {
      this.props.onReady(args);
    }
  };

  onEnded = (args: { event: SyntheticEvent<HTMLVideoElement> }): void => {
    this.logFourthQuartileEvent();
    this.seenQuartiles = new Set();
    // For looping videos start a new interval
    // This is to make sure in PLAYING logs endTime - time == videoEndTime - videoStartTime
    if (this.props.loop) {
      this.startNewInterval();
      // Without this line videoStartTime is set to the very end of the video
      this.currentInterval.videoStartTime = 0;
    }
    if (this.props.onEnded) {
      this.props.onEnded(args);
    }
  };

  handleVisibilityChange: IntersectionObserverCallback = (entries, observer) => {
    if (entries.length === 0) {
      return;
    }
    // Last entry should represent latest intersection change.
    const lastEntry = entries.slice(-1)[0];
    const viewability = floorRoundIntersectionRatio(
      Number.parseFloat(lastEntry.intersectionRatio.toFixed(2)),
    );

    if (
      viewability != null &&
      viewability !== this.currentInterval.viewability &&
      // Ignore viewability changes due to entering fullscreen
      !this.fullscreen
    ) {
      // TODO jjea: we need to do a larger refactor to combine PinVideoWithVisibility.js and this file.
      // Currently, we have 2 IntersectionObservers that handle 2 things: one for pausing, one for video logging.
      if (viewability === 0) {
        this.startNewInterval({ viewability, playbackState: VideoPlaybackState.PAUSED });
      } else {
        this.startNewInterval({ viewability });
      }
    }
  };

  onFullscreenChange = ({ event, fullscreen }: { event: Event, fullscreen: boolean }) => {
    // If transitioning in and out of fullscreen end current VIDEO_PlAYBACK_INTERVAL and start new one updated height/width
    if (fullscreen) {
      const { height, width } = window.screen;
      this.startNewInterval({ height, width, windowHeight: height, windowWidth: width });
    } else if (!fullscreen) {
      this.startNewInterval(this.getDimensions());
    }
    this.fullscreen = fullscreen;

    if (this.props.onFullscreenChange) {
      this.props.onFullscreenChange({ event, fullscreen });
    }
  };

  // When the browser window unloads such as closing the tab or completely exiting the browser
  // we should end the current interval and log it.
  handleClose = () => {
    // flush current interval
    this.startNewInterval();
  };

  handleContextMenu = (event: SyntheticEvent<HTMLDivElement>) => {
    // TODO (NF-195) Remove this handler when we stream videos through canvas/MediaSource
    event.preventDefault();
  };

  render() {
    const { contextLogData, inAdsDesktopVideoExperiment, rootMargin, ...videoProps } = this.props;
    const { seeking } = this.state;

    videoProps.onDurationChange = this.handleDurationChange;
    videoProps.onReady = this.handleReady;
    videoProps.onTimeChange = this.handleTimeChange;
    videoProps.onEnded = this.onEnded;
    videoProps.onFullscreenChange = this.onFullscreenChange;
    videoProps.onPlayheadDown = this.handlePlayheadDown;
    videoProps.onPlayheadUp = this.handlePlayheadUp;

    // We pause the video if the user is seeking
    videoProps.playing = !seeking && this.props.playing;

    const intersectionObserverOptions = inAdsDesktopVideoExperiment
      ? GRANULAR_INTERSECTION_OBSERVER_OPTIONS
      : INTERSECTION_OBSERVER_OPTIONS;

    // Each surface has different things in the header area so a custom rootMargin may be provided.
    if (rootMargin !== undefined) {
      intersectionObserverOptions.rootMargin = rootMargin;
    }

    return (
      <IntersectionObserver
        options={intersectionObserverOptions}
        onVisibilityChange={this.handleVisibilityChange}
      >
        <div ref={this.videoWrapperRef} onContextMenu={this.handleContextMenu}>
          <VideoWrapper {...videoProps} />
        </div>
      </IntersectionObserver>
    );
  }
}
